import parseInvestigationSchema from "$routes/patient/[:patientId]/_lib/investigations/parse-investigation-schema";
import type { TranslatedInvestigationCatalogueItem } from "$routes/patient/[:patientId]/investigations/_lib/util";
import { useMemo, useRef } from "react";
import * as Yup from "yup";

type Schemas = Record<string, Yup.AnyObjectSchema>;

export type InvestigationFormItem = {
  id: string;
  catalogueItem: TranslatedInvestigationCatalogueItem | null; // Allow null for unfilled investigations
  outcome: {
    id: string;
    meta: { updated?: string; created?: string };
    clinic: { id: string; name: string };
    orderedProperties: unknown[];
    organisation: { id: string; name: string };
    performer: { id: string; name: string };
  } | null;
  isRequested?: boolean;
};

export type FormValues = {
  investigations: InvestigationFormItem[];
};

// The inferred type is much more concreate than AnyObjectSchema
export function useValidationSchema() {
  const schemas = useRef({} as Schemas);

  return useMemo(
    () =>
      Yup.object({
        investigations: Yup.array(
          Yup.lazy((item) => {
            // If the form has not yet been interacted with,
            // return an empty schema, so child fields are not required
            if (!item?.catalogueItem || item.isRequested) {
              return Yup.object({
                catalogueItem: Yup.object(),
                outcome: Yup.object(),
                id: Yup.string().required(),
                isRequested: Yup.boolean().nullable(),
              });
            }

            const { catalogueItem } = item;
            const { _id: investigationId } = catalogueItem;

            if (!schemas.current[investigationId]) {
              const schema = parseInvestigationSchema(catalogueItem);
              schemas.current[investigationId] = Yup.object({
                catalogueItem: Yup.object().required(),
                outcome: schema.nullable(),
                id: Yup.string().required(),
                isRequested: Yup.boolean().nullable(),
              });
            }

            return schemas.current[investigationId];
          }),
        ).min(1),
      }),
    [],
  );
}
